import { onlineManager } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import '@/styles/app.css';
import { createQueryClient } from '@hlt/web-kit/api';
import { configureKit } from '@hlt/web-kit/config';
import { createI18n, initialLocale } from '@hlt/web-kit/i18n';
import { deviceActor, registerDeviceCleanup, registerPendingWork } from '@hlt/web-kit/session';
import { applyTheme, storedTheme } from '@hlt/web-kit/theme';
import { origins } from '@/app/origins';
import { Providers } from '@/app/providers';
import { createAppRouter } from '@/app/router';
import { clearLocalDrafts, countLocalDrafts } from '@/lib/offline/local-drafts';
import { registerAppShell } from '@/lib/offline/app-shell';
import { clearOfflineQueries, restoreOfflineQueries } from '@/lib/offline/query-snapshot';

// The 5S tool serves its own links; account, organization and sign-in pages live in the platform app.
configureKit({
    serves: ['fiveS'],
    origins: { platform: origins.platform, fiveS: window.location.origin },
    siteOrigin: origins.site,
    apiOrigin: origins.api,
    inAppPath: (link) => link.path,
});
// Offline drafts and the offline copy of opened audits live in this origin's storage; sign-out and
// account switches remove them. Drafts are unsent work: a sign-out from another app asks before removing
// them, while the offline copy goes at once.
registerDeviceCleanup(clearLocalDrafts, { unsentWork: true });
registerDeviceCleanup(clearOfflineQueries);
registerPendingWork(countLocalDrafts);

const queryClient = createQueryClient();
const locale = initialLocale();
// The interface language is downloaded on its own (offline: from the service worker); the first render
// waits for it below.
const i18nReady = createI18n(locale);
applyTheme(storedTheme());
const router = createAppRouter(queryClient);
// Without a connection the app opens what this person opened before: the page comes from the service worker,
// the data from the offline copy of the device's last account, restored before the first render. Online, the
// session is verified first: restoring then could show the previous account's data on a shared device and
// hide the account switch that removes it.
// The query library assumes a connection until the first offline event, so a page opened offline would
// treat every stored read as failed; starting from the browser's state pauses those reads instead.
onlineManager.setOnline(navigator.onLine);
const actor = deviceActor();
if (actor && !navigator.onLine) await restoreOfflineQueries(queryClient, actor);
if (import.meta.env.PROD) registerAppShell();
const i18n = await i18nReady;

const root = document.getElementById('root');
if (root) {
    createRoot(root).render(
        <StrictMode>
            <Providers queryClient={queryClient} i18n={i18n}>
                <RouterProvider router={router} />
            </Providers>
        </StrictMode>,
    );
}
